/**
 * DesignColorsMenu.tsx - Color Theme Selection and Customization Component
 *
 * This component provides comprehensive color theming capabilities for fundraisers
 * including preset color templates and custom color editing with validation.
 *
 * Key Features:
 * - Preset color template selection with visual swatches
 * - Custom color editing with hex color validation
 * - Real-time color preview and application
 * - Color picker integration for easy selection
 * - Validation and error handling for color inputs
 * - State management for custom vs template colors
 *
 * Color System:
 * - Background: Page and component backgrounds
 * - Primary: Main brand color for buttons and highlights
 * - Secondary: Accent color for secondary elements
 * - Accent: Subtle backgrounds and borders
 * - Text: Primary text color with automatic contrast
 *
 * Template Options:
 * - Default: Clean blue and green theme
 * - Ocean: Blue-focused aquatic theme
 * - Forest: Green nature-inspired theme
 * - Sunset: Warm orange and yellow theme
 * - Purple: Purple and violet theme
 * - Pink: Pink and rose theme
 *
 * Props: None (uses MobX store for state management)
 *
 * Dependencies: Color templates, hex validation, color picker, MobX store
 */

import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useRef,
} from "react";
import styled from "styled-components";
import { Paintbrush } from "lucide-react";
import {
  colorSwatches,
  colorTemplates,
  ColorTemplate,
} from "./designConstants";
import { CustomColorConfig } from "../../../interfaces";
import { ColorSwatches } from "../../../enums";
import {
  ColorModel,
  FundraiserCustomColorConfig,
  FundraiserDonationButtonColors,
} from "../../../models";
import {
  FlexContainer,
  ColorPicker as ColorPickerComponent,
  IColor,
} from "@mindmemobile/mm-shared-global";
import { SelectedColorButton } from "../../gallery-settings/GalleryAppearanceSection";
import { fundraiserStore } from "../../../stores";

// State management interfaces

interface ColorMenuState {
  currentState: "main" | "customize" | "custom-selected";
  selectedTemplate: string | null;
  customColors: CustomColorConfig;
  hasCustomColors: boolean;
  tempCustomColors: CustomColorConfig;
}

type ColorMenuStateType = "main" | "customize" | "custom-selected";

// Color validation and normalization utilities
const validateHexColor = (color: string): boolean => {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
};

const normalizeHexColor = (color: ColorModel): string => {
  // Remove # if present
  const cleanColor = color.hex.replace("#", "");

  // If 3-digit hex, expand to 6-digit
  if (cleanColor.length === 3) {
    return (
      "#" +
      cleanColor[0] +
      cleanColor[0] +
      cleanColor[1] +
      cleanColor[1] +
      cleanColor[2] +
      cleanColor[2]
    );
  }

  // If 6-digit hex, add # if missing
  if (cleanColor.length === 6) {
    return "#" + cleanColor;
  }

  // Return original if invalid
  return color.hex;
};

const sanitizeColorInput = (color: ColorModel): string => {
  const normalized = normalizeHexColor(color);
  return validateHexColor(normalized) ? normalized : color.hex;
};

const SubMenuContent = styled.div`
  padding: 8px 12px 12px 12px;
  background: #ffffff;

  .checkout-color-picker {
    position: absolute !important;
    top: auto;
    left: auto;
    transform: translate(-1px, 190px);
    z-index: 9999 !important;
    #rbgcp-wrapper {
      // this is for the height width of the color picker box inside the color picker component
      > div:first-child > div:nth-child(2) > div:nth-child(2) {
        height: 150px !important;
      }
    }
  }
`;

const ColorsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SwatchContainer = styled.button<{ selected: boolean }>`
  display: flex;
  flex-direction: column;
  border-radius: 6px;
  border: 2px solid ${(props) => (props.selected ? "#3b82f6" : "#e2e8f0")};
  cursor: pointer;
  overflow: hidden;
  transition: all 0.2s ease;
  background: transparent;
  padding: 0;
  width: 100%;
  height: 20px;

  &:hover {
    border-color: #3b82f6;
    transform: translateY(-2px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
`;

const SwatchColorRow = styled.div`
  display: flex;
  height: 10px;
`;

const SwatchColor = styled.div<{ color: string }>`
  flex: 1;
  background: ${(props) => props.color};
`;

const CustomColorTitle = styled.h4`
  font-size: 11px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
`;

const ColorInputGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 6px;
`;

const ColorInputItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ColorInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ColorInputLabel = styled.label`
  font-size: 8px;
  font-weight: 600;
  color: #374151;
  text-transform: capitalize;
  min-width: 50px;
`;

const ColorPicker = styled.input`
  width: 20px;
  height: 20px;
  border: 2px solid #e2e8f0;
  border-radius: 4px;
  cursor: pointer;
  background: transparent;
  flex-shrink: 0;

  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }

  &::-webkit-color-swatch {
    border: none;
    border-radius: 2px;
  }
`;

const ColorTextInput = styled.input`
  flex: 1;
  padding: 3px 5px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 8px;
  font-family: monospace;
  color: #1e293b;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`;

// Main Colors Interface Styled Components
const MainColorsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ColorTemplateCard = styled.button<{ selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-radius: 12px;
  border: 2px solid ${(props) => (props.selected ? "#3B82F6" : "#E2E8F0")};
  background: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  min-height: 54px;

  &:hover {
    border-color: #3b82f6;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const ColorSwatchRow = styled.div`
  display: flex;
  gap: 2px;
`;

const ColorSwatch = styled.div<{ color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${(props) => props.color};
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const TemplateName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  flex: 1;
  text-align: left;
`;

const CustomizeColorsLink = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  margin-top: 4px;
  gap: 8px;
  color: #1e293b;

  &:hover {
    color: #475569;
  }
`;

const CustomizeLinkText = styled.span`
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
`;

// Customize Colors Interface Styled Components
const CustomizeColorsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 12px;
  margin: 8px 0;
`;

const CustomizeHeader = styled.div`
  text-align: center;
  padding: 16px 0 8px;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 8px;
`;

const CustomizeTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const ColorFieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ColorField = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  .checkout-color-picker {
    position: absolute !important;
    top: auto;
    left: auto;
    transform: translate(-1px, 190px);
    z-index: 9999 !important;
    #rbgcp-wrapper {
      // this is for the height width of the color picker box inside the color picker component
      > div:first-child > div:nth-child(2) > div:nth-child(2) {
        height: 150px !important;
      }
    }
  }
`;

const ColorFieldLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  min-width: 80px;
  text-align: left;
`;

const ColorInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  .background-color-picker {
    transform: translate(53px, 140px);
  }
  .primary-color-picker {
    transform: translate(53px, 85px);
  }
  .secondary-color-picker {
    transform: translate(53px, 20px);
  }
  .accent-color-picker {
    transform: translate(53px, -40px);
  }
  .text-color-picker {
    transform: translate(53px, -90px);
  }
  #rbgcp-wrapper {
    // this is for the height width of the color picker box inside the color picker component

    > div:first-child > div:first-child {
      height: 165px !important;
    }
  }
`;

const ColorPickerInput = styled.input`
  width: 32px;
  height: 32px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  background: transparent;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }

  &::-webkit-color-swatch {
    border: none;
    border-radius: 6px;
  }
`;

const HexInput = styled.input<{ hasError?: boolean }>`
  padding: 8px 12px;
  border: 1px solid ${(props) => (props.hasError ? "#EF4444" : "#E2E8F0")};
  border-radius: 8px;
  font-size: 14px;
  font-family: monospace;
  color: #1e293b;
  width: 100px;
  background: ${(props) => (props.hasError ? "#FEF2F2" : "#FFFFFF")};

  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? "#EF4444" : "#3B82F6")};
    box-shadow: 0 0 0 2px
      ${(props) =>
        props.hasError ? "rgba(239, 68, 68, 0.1)" : "rgba(59, 130, 246, 0.1)"};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;

const BackButton = styled.button<{ disabled?: boolean }>`
  flex: 1;
  padding: 8px 24px;
  border-radius: 12px;
  border: 2px solid #e2e8f0;
  background: #ffffff;
  color: ${(props) => (props.disabled ? "#9CA3AF" : "#64748B")};
  font-size: 14px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};

  &:hover:not(:disabled) {
    border-color: #cbd5e1;
    background: #f8fafc;
  }
`;

const SaveButton = styled.button<{ disabled?: boolean }>`
  flex: 1;
  padding: 8px 24px;
  border-radius: 12px;
  border: none;
  background: ${(props) => (props.disabled ? "#9CA3AF" : "#3B82F6")};
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #2563eb;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }
`;

// Custom Selected Interface Styled Components
const CustomSelectedContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CustomColorCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-radius: 12px;
  border: 2px solid #3b82f6;
  background: #ffffff;
  width: 100%;
  min-height: 54px;
`;

const CustomSwatchRow = styled.div`
  display: flex;
  gap: 2px;
`;

const CustomSwatch = styled.div<{ color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${(props) => props.color};
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const CustomName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  flex: 1;
  text-align: left;
`;

const EditColorsButton = styled.button`
  padding: 6px 14px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #3b82f6;
    background: #ffffff;
    color: #3b82f6;
  }
`;

// Error handling and feedback styled components
const ErrorMessage = styled.div`
  padding: 8px 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 12px;
  font-weight: 500;
  margin-top: 8px;
`;

const FieldError = styled.div`
  color: #dc2626;
  font-size: 11px;
  margin-top: 4px;
`;

interface DesignColorsMenuProps {
  selectedColorSwatch: keyof typeof ColorSwatches;
  customColors: FundraiserCustomColorConfig;
  hasCustomColors: boolean;
  onColorSwatchSelect: (swatchId: keyof typeof ColorSwatches) => void;
  onCustomColorPreview?: (colorType: string, value: string) => void;
}

export interface DesignColorsMenuRef {
  discardUnsavedChanges: () => void;
  hasUnsavedChanges: () => boolean;
}

export const DesignColorsMenu: React.FC<DesignColorsMenuProps> = ({
  selectedColorSwatch,
  customColors,
  hasCustomColors: hasCustomColorsFromParent,
  onColorSwatchSelect,
  onCustomColorPreview,
}) => {
  const [activePicker, setActivePicker] = useState<string | null>(null);
  // State management for three-state color menu
  const [colorMenuState, setColorMenuState] = useState<ColorMenuStateType>(
    selectedColorSwatch === ColorSwatches.Custom ? "custom-selected" : "main",
  );
  const [hasCustomColors, setHasCustomColors] = useState(
    selectedColorSwatch === ColorSwatches.Custom,
  );
  const [tempCustomColors, setTempCustomColors] =
    useState<FundraiserCustomColorConfig>(new FundraiserCustomColorConfig());

  // TEST: Simple separate state
  const [testColor, setTestColor] = useState("#12B981");

  // Track original colors when entering customize mode
  const [originalCustomColors, setOriginalCustomColors] =
    useState<FundraiserCustomColorConfig>(new FundraiserCustomColorConfig());

  // Error handling and user feedback states
  const [saveError, setSaveError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    setHasCustomColors(selectedColorSwatch === ColorSwatches.Custom);
    setColorMenuState(
      selectedColorSwatch === ColorSwatches.Custom
        ? "custom-selected"
        : colorMenuState,
    );
  }, [selectedColorSwatch]);

  const ColorPickerField: React.FC<{
    label: string;
    colorKey: keyof FundraiserCustomColorConfig;
    value: ColorModel;
    className?: string;
  }> = ({ label, colorKey, value, className }) => (
    <ColorField>
      <ColorFieldLabel>{label}</ColorFieldLabel>
      <ColorInputContainer>
        <SelectedColorButton
          color={value.rgba || value.hex}
          onClick={() =>
            setActivePicker(activePicker === colorKey ? null : colorKey)
          }
          className="color-picker-gallery"
        >
          <div />
        </SelectedColorButton>

        <HexInput
          type="text"
          value={value.hex}
          onChange={(e) =>
            handleColorChange(
              colorKey,
              new ColorModel({ hex: e.target.value, rgba: e.target.value }),
            )
          }
          placeholder="#000000"
          onClick={(e) => e.stopPropagation()}
        />

        {activePicker === colorKey && (
          <ColorPickerComponent
            color={value}
            onChange={(color) => handleColorChange(colorKey, color)}
            onHide={() => setActivePicker(null)}
            // className="checkout-color-picker"
            className={`${className} checkout-color-picker`}
          />
        )}
      </ColorInputContainer>
    </ColorField>
  );

  // Track if any changes have been made
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const handleColorChange = (
    key: keyof FundraiserCustomColorConfig,
    color: IColor,
  ) => {
    const newColor = new ColorModel(color);
    fundraiserStore.updateBuilderField("customColors", {
      ...customColors,
      [key]: newColor,
    });

    // Sync Primary/Secondary custom color changes to donationButtonColors so
    // donate buttons (CallToAction, Header, embed views, Promoter Invite form,
    // DesignPreview) reflect the custom color immediately. Other color keys
    // (background, accent, text) must not affect donate button styling.
    if (key === "primary" || key === "secondary") {
      const currentDonationButtonColors =
        fundraiserStore.builderData.donationButtonColors ||
        new FundraiserDonationButtonColors();
      fundraiserStore.updateBuilderField("donationButtonColors", {
        ...currentDonationButtonColors,
        [key]: newColor,
      });
    }
  };

  // Define handleDiscardChanges first
  const handleDiscardChanges = () => {
    // Reset to original colors without saving
    setTempCustomColors(originalCustomColors);

    // Reset parent component colors to original values
    Object.keys(originalCustomColors).forEach((colorType) => {
      fundraiserStore.updateBuilderField("customColors", {
        ...customColors,
        [colorType]:
          originalCustomColors[colorType as keyof typeof originalCustomColors],
      });
    });

    setHasUnsavedChanges(false);

    // If we have custom colors saved, make sure they're still selected
    if (hasCustomColors) {
      onColorSwatchSelect(ColorSwatches.Custom);
    }

    // Always go back to main menu
    setColorMenuState("main");
  };

  // State transition functions
  const transitionToCustomize = () => {
    // Store original colors when entering customize mode
    setOriginalCustomColors(new FundraiserCustomColorConfig());
    setHasUnsavedChanges(false);
    setColorMenuState("customize");
    fundraiserStore.updateBuilderField(
      "selectedColorSwatch",
      ColorSwatches.Custom,
    );
  };

  const transitionToMain = () => {
    setColorMenuState("main");
    setHasCustomColors(false);
  };

  const transitionToCustomSelected = () => {
    setColorMenuState("custom-selected");
    setHasCustomColors(true);
  };

  const handleEditCustomColors = () => {
    // Ensure temp colors are populated with current custom colors
    setTempCustomColors(new FundraiserCustomColorConfig());

    setColorMenuState("customize");
  };

  const handleBackToMain = () => {
    // If there are unsaved changes, discard them first
    if (hasUnsavedChanges) {
      handleDiscardChanges();
    } else {
      // Just go back to main menu without changes
      setColorMenuState("main");
    }
  };

  const handleTemplateSelect = (template: ColorTemplate) => {
    try {
      // Clear any existing errors
      setSaveError(null);
      setValidationErrors({});

      // Update the selected swatch first - this will trigger the DesignStep handleColorSwatchSelect
      onColorSwatchSelect(template.id as keyof typeof ColorSwatches);

      // Update local state to match template
      setTempCustomColors(new FundraiserCustomColorConfig(template.colors));
      setOriginalCustomColors(template.colors);

      // Clear custom colors state since we're using a template
      setHasCustomColors(false);
      setHasUnsavedChanges(false);

      // Always go back to main menu to show selection
      setColorMenuState("main");
    } catch (error) {
      setSaveError("Failed to apply color template. Please try again.");
      console.error("Error applying color template:", error);
    }
  };

  // Main Colors Interface Component
  const MainColorsInterface: React.FC = () => (
    <MainColorsContainer>
      {colorTemplates.map((template) => (
        <ColorTemplateCard
          key={template.id}
          selected={selectedColorSwatch === template.id && !hasCustomColors}
          onClick={() => handleTemplateSelect(template)}
        >
          <ColorSwatchRow>
            {template.swatches.map((color, index) => (
              <ColorSwatch key={index} color={color} />
            ))}
          </ColorSwatchRow>
          <TemplateName>{template.name}</TemplateName>
        </ColorTemplateCard>
      ))}

      {hasCustomColors ? (
        <ColorTemplateCard
          selected={selectedColorSwatch === ColorSwatches.Custom}
          onClick={() => {
            onColorSwatchSelect(ColorSwatches.Custom);
          }}
        >
          <ColorSwatchRow>
            <ColorSwatch
              color={
                originalCustomColors.background.rgba ||
                originalCustomColors.background.hex
              }
            />
            <ColorSwatch
              color={
                originalCustomColors.primary.rgba ||
                originalCustomColors.primary.hex
              }
            />
            <ColorSwatch
              color={
                originalCustomColors.secondary.rgba ||
                originalCustomColors.secondary.hex
              }
            />
            <ColorSwatch
              color={
                originalCustomColors.accent.rgba ||
                originalCustomColors.accent.hex
              }
            />
            <ColorSwatch
              color={
                originalCustomColors.text.rgba || originalCustomColors.text.hex
              }
            />
          </ColorSwatchRow>
          <TemplateName>Custom</TemplateName>
        </ColorTemplateCard>
      ) : (
        <CustomizeColorsLink onClick={transitionToCustomize}>
          <Paintbrush size={16} />
          <CustomizeLinkText>Customize Colors</CustomizeLinkText>
        </CustomizeColorsLink>
      )}

      {hasCustomColors && (
        <CustomizeColorsLink onClick={transitionToCustomize}>
          <Paintbrush size={16} />
          <CustomizeLinkText>Edit Colors</CustomizeLinkText>
        </CustomizeColorsLink>
      )}
    </MainColorsContainer>
  );

  // Use refs to collect colors without causing re-renders
  const colorRefs = useRef<Record<string, string>>({
    background:
      customColors.background.rgba || customColors.background.hex || "#FFFFFF",
    primary: customColors.primary.rgba || customColors.primary.hex || "#12B981",
    secondary:
      customColors.secondary.rgba || customColors.secondary.hex || "#3986EF",
    accent: customColors.accent.rgba || customColors.accent.hex || "#ECFFFC",
    text: customColors.text.rgba || customColors.text.hex || "#1E293B",
  });

  const CustomizeColorsInterface: React.FC = () => (
    <div>
      <CustomizeHeader>
        <CustomizeTitle>Customize Colors</CustomizeTitle>
      </CustomizeHeader>
      <CustomizeColorsContainer>
        <ColorFieldContainer>
          <ColorPickerField
            label="Background"
            colorKey="background"
            value={customColors.background}
            className="background-color-picker"
          />
          <ColorPickerField
            label="Primary"
            colorKey="primary"
            value={customColors.primary}
            className="primary-color-picker"
          />
          <ColorPickerField
            label="Secondary"
            colorKey="secondary"
            value={customColors.secondary}
            className="secondary-color-picker"
          />
          <ColorPickerField
            label="Accent"
            colorKey="accent"
            value={customColors.accent}
            className="accent-color-picker"
          />
          <ColorPickerField
            label="Text"
            colorKey="text"
            value={customColors.text}
            className="text-color-picker"
          />
        </ColorFieldContainer>
        <ButtonContainer>
          <BackButton onClick={handleBackToMain}>Cancel</BackButton>
          <SaveButton
            onClick={() => {
              // Save all colors using the regular handler (which updates hasCustomColors)
              onColorSwatchSelect(ColorSwatches.Custom);
              // Go back to main menu
              setColorMenuState("custom-selected");
            }}
          >
            Save
          </SaveButton>
        </ButtonContainer>
      </CustomizeColorsContainer>
    </div>
  );

  // Custom Selected Interface Component
  const CustomSelectedInterface: React.FC = () => (
    <CustomSelectedContainer>
      {colorTemplates.map((template) => (
        <ColorTemplateCard
          key={template.id}
          selected={selectedColorSwatch === template.id && !hasCustomColors}
          onClick={() => handleTemplateSelect(template)}
        >
          <ColorSwatchRow>
            {template.swatches.map((color, index) => (
              <ColorSwatch key={index} color={color} />
            ))}
          </ColorSwatchRow>
          <TemplateName>{template.name}</TemplateName>
        </ColorTemplateCard>
      ))}

      {hasCustomColors && (
        <CustomColorCard>
          <CustomSwatchRow>
            <CustomSwatch
              color={
                customColors.background.rgba || customColors.background.hex
              }
            />
            <CustomSwatch
              color={customColors.primary.rgba || customColors.primary.hex}
            />
            <CustomSwatch
              color={customColors.secondary.rgba || customColors.secondary.hex}
            />
            <CustomSwatch
              color={customColors.accent.rgba || customColors.accent.hex}
            />
            <CustomSwatch
              color={customColors.text.rgba || customColors.text.hex}
            />
          </CustomSwatchRow>
          <CustomName>Custom</CustomName>
          <EditColorsButton onClick={handleEditCustomColors}>
            Edit
          </EditColorsButton>
        </CustomColorCard>
      )}
    </CustomSelectedContainer>
  );

  // Render appropriate interface based on current state
  const renderCurrentInterface = () => {
    switch (colorMenuState) {
      case "main":
        return <MainColorsInterface />;
      case "customize":
        return <CustomizeColorsInterface />;
      case "custom-selected":
        return <CustomSelectedInterface />;
      default:
        return <MainColorsInterface />;
    }
  };

  return <SubMenuContent>{renderCurrentInterface()}</SubMenuContent>;
};
